import { create } from 'zustand';
import { useEffect } from 'react';
import { cn } from '../lib/cn.js';

export type ToastKind = 'info' | 'success' | 'error';

interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  ttl: number; // ms
}

interface ToastStore {
  items: Toast[];
  push: (kind: ToastKind, message: string, ttl?: number) => void;
  remove: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set, get) => ({
  items: [],
  push: (kind, message, ttl = 4000) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    set({ items: [...get().items, { id, kind, message, ttl }] });
    setTimeout(() => get().remove(id), ttl);
  },
  remove: (id) => set({ items: get().items.filter((t) => t.id !== id) }),
}));

// Удобные шорткаты.
export const toast = {
  info: (m: string) => useToastStore.getState().push('info', m),
  success: (m: string) => useToastStore.getState().push('success', m),
  error: (m: string, ttl = 6000) => useToastStore.getState().push('error', m, ttl),
};

export function ToastContainer() {
  const items = useToastStore((s) => s.items);
  const remove = useToastStore((s) => s.remove);

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 items-center pointer-events-none"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {items.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  useEffect(() => {
    // анимацию обрабатывает CSS, но оставим no-op
  }, []);
  const style: Record<ToastKind, string> = {
    info: 'bg-neutral-800 text-neutral-100 border-neutral-700',
    success: 'bg-emerald-700/90 text-emerald-50 border-emerald-600',
    error: 'bg-red-700/90 text-red-50 border-red-600',
  };
  return (
    <button
      type="button"
      onClick={onClose}
      className={cn(
        'pointer-events-auto max-w-sm px-4 py-2.5 rounded-xl border shadow-lg text-sm text-left',
        'animate-[toast-in_0.2s_ease-out]',
        style[toast.kind],
      )}
    >
      {toast.message}
    </button>
  );
}
